import { DownOutlined } from '@ant-design/icons';
import { DatePicker, Popover, Select } from 'antd';
import { ScopeCheckbox } from 'components/Custom/checkbox';
import { FormSelect } from 'components/Custom/selects';
import { Dayjs } from 'dayjs';
import {
  formatDisplayDate,
  formatDisplayDateRange,
  getLocalizedDatePickerFormat,
} from 'i18n/calendarLabels';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { DateOptions } from 'store/api/enums';
import { getDateOptionLabel } from 'utils/display-enum-label';
import {
  ApiDateRange,
  getApiDateRangeForDateOption,
  getDayjsRangeForDateOption,
} from 'utils/date-option-range';
import {
  DEFAULT_DRAWER_DATE_OPTION,
  DRAWER_DATE_DISPLAY_FORMAT,
  DRAWER_DATE_FILTER_OPTIONS,
  FALLBACK_DRAWER_DATE_OPTION,
  countItemsInDateRange,
  getDrawerDateRangeStateForOption,
  resolveDrawerDateRangeFromCustom,
} from 'utils/drawer-date-range';

const { RangePicker } = DatePicker;

export type DrawerDateRangeFilterProps = {
  dateRangeOption: DateOptions | undefined;
  enableCustomRange: boolean;
  customRange: [Dayjs | null, Dayjs | null];
  onDateOptionChange: (value: DateOptions) => void;
  onCustomRangeToggle: (enabled: boolean) => void;
  onCustomRangeChange: (range: [Dayjs | null, Dayjs | null]) => void;
  /** Bordered panel (drawer stats). `inline` = table-style FilterSelect pill. */
  variant?: 'default' | 'compact' | 'inline';
  periodLabelKey?: string;
  className?: string;
  /** Height for inline FilterSelect pill (match adjacent search input). */
  inlineHeight?: string;
};

export const DrawerDateRangeFilter: React.FC<DrawerDateRangeFilterProps> = ({
  dateRangeOption,
  enableCustomRange,
  customRange,
  onDateOptionChange,
  onCustomRangeToggle,
  onCustomRangeChange,
  variant = 'default',
  periodLabelKey = 'organisation.capture-statistics-select-period',
  className,
  inlineHeight = '38px',
}) => {
  const { t, i18n } = useTranslation();
  const [popupOpen, setPopupOpen] = React.useState(false);
  const rangePickerValue =
    customRange[0] && customRange[1] ? customRange : undefined;

  const isActive =
    enableCustomRange && customRange[0] && customRange[1]
      ? true
      : Boolean(dateRangeOption && dateRangeOption !== DateOptions.ALL);

  const displayValue =
    enableCustomRange && customRange[0] && customRange[1]
      ? formatDisplayDateRange(
          customRange[0],
          customRange[1],
          DRAWER_DATE_DISPLAY_FORMAT,
          i18n.language
        )
      : dateRangeOption && dateRangeOption !== DateOptions.ALL
      ? getDateOptionLabel(dateRangeOption, t)
      : undefined;

  const handleInlinePresetChange = (value: DateOptions) => {
    onDateOptionChange(value);
    setPopupOpen(false);
  };

  const handleInlineCustomRangeChange = (
    range: [Dayjs | null, Dayjs | null] | null
  ) => {
    const nextRange = (range as [Dayjs | null, Dayjs | null]) ?? [null, null];
    onCustomRangeChange(nextRange);
    if (nextRange[0] && nextRange[1]) {
      setPopupOpen(false);
    }
  };

  const handlePopoverOpenChange = (open: boolean) => {
    if (
      !open &&
      enableCustomRange &&
      !(customRange[0] && customRange[1])
    ) {
      onCustomRangeToggle(false);
    }
    setPopupOpen(open);
  };

  const dateFilterLabel = t('transactions.transactionDate');

  const renderInlinePopup = () => (
    <div
      className="drawer-date-range-filter__inline-panel"
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <div className="drawer-date-range-filter__preset-list" role="listbox">
        {DRAWER_DATE_FILTER_OPTIONS.map((value) => (
          <button
            key={value}
            type="button"
            role="option"
            aria-selected={dateRangeOption === value}
            className={[
              'drawer-date-range-filter__preset-option',
              dateRangeOption === value
                ? 'drawer-date-range-filter__preset-option--selected'
                : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => handleInlinePresetChange(value)}
          >
            {getDateOptionLabel(value, t)}
          </button>
        ))}
      </div>
      <div className="drawer-date-range-filter__inline-footer">
        <ScopeCheckbox
          checked={enableCustomRange}
          onChange={(e) => onCustomRangeToggle(e.target.checked)}
        >
          {t('main.custom-range')}
        </ScopeCheckbox>
        {enableCustomRange ? (
          <RangePicker
            className="drawer-date-range-filter__inline-range-picker"
            value={rangePickerValue}
            format={getLocalizedDatePickerFormat(
              DRAWER_DATE_DISPLAY_FORMAT,
              i18n.language
            )}
            placement="bottomRight"
            onChange={handleInlineCustomRangeChange}
            getPopupContainer={() => document.body}
          />
        ) : null}
      </div>
    </div>
  );

  if (variant === 'inline') {
    const triggerLabel = displayValue ?? dateFilterLabel;

    return (
      <div
        className="drawer-date-range-filter-inline"
        style={{ height: inlineHeight }}
      >
        <Popover
          content={renderInlinePopup()}
          trigger="click"
          open={popupOpen}
          onOpenChange={handlePopoverOpenChange}
          placement="bottomRight"
          overlayClassName="drawer-date-range-filter-popover"
          overlayStyle={{ maxWidth: 'calc(100vw - 16px)' }}
          getPopupContainer={() => document.body}
          arrow={false}
          autoAdjustOverflow
          destroyOnHidden={true}
        >
          <button
            type="button"
            className={[
              'drawer-date-range-filter-inline-trigger',
              isActive ? 'drawer-date-range-filter-inline-trigger--active' : '',
              !displayValue
                ? 'drawer-date-range-filter-inline-trigger--placeholder'
                : '',
              className,
            ]
              .filter(Boolean)
              .join(' ')}
            aria-label={dateFilterLabel}
            aria-expanded={popupOpen}
          >
            <span className="drawer-date-range-filter-inline-trigger__label">
              {triggerLabel}
            </span>
            <DownOutlined className="drawer-date-range-filter-inline-trigger__icon" />
          </button>
        </Popover>
      </div>
    );
  }

  return (
    <div
      className={[
        'drawer-date-range-filter',
        variant === 'compact' ? 'drawer-date-range-filter--compact' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="drawer-date-range-filter__period-row">
        <span className="drawer-date-range-filter__period-label">
          {t(periodLabelKey)}
        </span>
        <div className="drawer-date-range-filter__period-control">
          <FormSelect
            width="100%"
            height="35px"
            radius="10px"
            isFilter
            placeholder={t('main.date-range')}
            style={{
              backgroundColor: 'var(--color-surface, #F3F7F8)',
              borderColor: '#cccccc',
              fontSize: 13,
            }}
            onChange={(value) => onDateOptionChange(value as DateOptions)}
            value={dateRangeOption || undefined}
          >
            {DRAWER_DATE_FILTER_OPTIONS.map((value) => (
              <Select.Option key={value} value={value}>
                {getDateOptionLabel(value, t)}
              </Select.Option>
            ))}
          </FormSelect>
        </div>
        <ScopeCheckbox
          className="drawer-date-range-filter__custom-range"
          checked={enableCustomRange}
          onChange={(e) => onCustomRangeToggle(e.target.checked)}
        >
          {t('main.custom-range')}
        </ScopeCheckbox>
      </div>
      {enableCustomRange ? (
        <RangePicker
          className="drawer-date-range-filter__range-picker"
          style={{ borderRadius: '10px', height: '34px', width: '100%' }}
          value={rangePickerValue}
          format={getLocalizedDatePickerFormat(
            DRAWER_DATE_DISPLAY_FORMAT,
            i18n.language
          )}
          onChange={(date) =>
            onCustomRangeChange(
              (date as [Dayjs | null, Dayjs | null]) ?? [null, null]
            )
          }
        />
      ) : null}
    </div>
  );
};

type UseDrawerDateRangeFilterOptions<T extends { uploadDate?: unknown }> = {
  defaultOption?: DateOptions;
  fallbackOption?: DateOptions;
  /** When true, expands to fallback preset if default yields no matching items. */
  enableAutoFallback?: boolean;
  items?: T[];
  onApply?: (range: ApiDateRange) => void;
};

export function useDrawerDateRangeFilter<T extends { uploadDate?: unknown }>({
  defaultOption = DEFAULT_DRAWER_DATE_OPTION,
  fallbackOption = FALLBACK_DRAWER_DATE_OPTION,
  enableAutoFallback = false,
  items,
  onApply,
}: UseDrawerDateRangeFilterOptions<T> = {}) {
  const initialState = React.useMemo(
    () => getDrawerDateRangeStateForOption(defaultOption),
    [defaultOption]
  );

  const [dateRangeOption, setDateRangeOption] = React.useState<
    DateOptions | undefined
  >(initialState.dateRangeOption);
  const [enableCustomRange, setEnableCustomRange] = React.useState(
    initialState.enableCustomRange
  );
  const [customRange, setCustomRange] = React.useState<
    [Dayjs | null, Dayjs | null]
  >(initialState.customRange);
  const [appliedRange, setAppliedRange] = React.useState<ApiDateRange>(
    initialState.appliedRange
  );
  const userChangedFilterRef = React.useRef(false);
  const autoFallbackAppliedRef = React.useRef(false);

  const applyDateRange = React.useCallback(
    (nextRange: ApiDateRange, nextOption?: DateOptions) => {
      setAppliedRange(nextRange);
      if (nextOption !== undefined) {
        setDateRangeOption(nextOption);
      }
      onApply?.(nextRange);
    },
    [onApply]
  );

  const handleDateOptionChange = React.useCallback(
    (value: DateOptions) => {
      userChangedFilterRef.current = true;
      setDateRangeOption(value);

      if (value === DateOptions.ALL) {
        setEnableCustomRange(false);
        setCustomRange([null, null]);
        applyDateRange({}, value);
        return;
      }

      const presetRange = getDayjsRangeForDateOption(value);
      if (presetRange) {
        setEnableCustomRange(false);
        setCustomRange(presetRange);
        applyDateRange(getApiDateRangeForDateOption(value), value);
      }
    },
    [applyDateRange]
  );

  const handleCustomRangeToggle = React.useCallback(
    (enabled: boolean) => {
      userChangedFilterRef.current = true;
      setEnableCustomRange(enabled);

      if (enabled) {
        return;
      }

      if (dateRangeOption && dateRangeOption !== DateOptions.ALL) {
        const presetRange = getDayjsRangeForDateOption(dateRangeOption);
        if (presetRange) {
          setCustomRange(presetRange);
          applyDateRange(
            getApiDateRangeForDateOption(dateRangeOption),
            dateRangeOption
          );
          return;
        }
      }

      setCustomRange([null, null]);
      applyDateRange({}, DateOptions.ALL);
    },
    [applyDateRange, dateRangeOption]
  );

  const handleCustomRangeChange = React.useCallback(
    (range: [Dayjs | null, Dayjs | null]) => {
      userChangedFilterRef.current = true;
      setCustomRange(range);

      if (range[0] && range[1]) {
        applyDateRange(resolveDrawerDateRangeFromCustom(range));
        return;
      }

      if (!range[0] && !range[1]) {
        if (dateRangeOption && dateRangeOption !== DateOptions.ALL) {
          applyDateRange(getApiDateRangeForDateOption(dateRangeOption));
          return;
        }
        setAppliedRange({});
      }
    },
    [applyDateRange, dateRangeOption]
  );

  React.useEffect(() => {
    if (
      !enableAutoFallback ||
      userChangedFilterRef.current ||
      autoFallbackAppliedRef.current ||
      !items?.length ||
      enableCustomRange ||
      dateRangeOption !== defaultOption
    ) {
      return;
    }

    const defaultRange = getApiDateRangeForDateOption(defaultOption);
    if (countItemsInDateRange(items, defaultRange) > 0) {
      return;
    }

    const fallbackState = getDrawerDateRangeStateForOption(fallbackOption);
    autoFallbackAppliedRef.current = true;
    setDateRangeOption(fallbackState.dateRangeOption);
    setEnableCustomRange(fallbackState.enableCustomRange);
    setCustomRange(fallbackState.customRange);
    setAppliedRange(fallbackState.appliedRange);
    onApply?.(fallbackState.appliedRange);
  }, [
    dateRangeOption,
    defaultOption,
    enableAutoFallback,
    enableCustomRange,
    fallbackOption,
    items,
    onApply,
  ]);

  const filterProps: DrawerDateRangeFilterProps = {
    dateRangeOption,
    enableCustomRange,
    customRange,
    onDateOptionChange: handleDateOptionChange,
    onCustomRangeToggle: handleCustomRangeToggle,
    onCustomRangeChange: handleCustomRangeChange,
  };

  return {
    appliedRange,
    filterProps,
    setAppliedRange,
    handleDateOptionChange,
    handleCustomRangeToggle,
    handleCustomRangeChange,
  };
}
